import * as React from 'react';

import { faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as z from 'zod';

import { type InParkingCard } from 'src/features/cards/types/card';
import { useGetParkingGuestReasons } from 'src/features/parkingManagement/api/ReasonsForGuest/useGetParkingGuestReasons';
import { ALLOWED_VALIDATION } from 'src/features/userManagement/types';
import { useValidateCardInParking } from 'src/features/validation/hooks/useValidateCardInParking';
import { validationMessages } from 'src/features/validation/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import { toast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { generalErrorMessages, generalMessages } from 'src/utils/messages';

import type { ValidatorSettings } from 'src/features/validation/types.ts';

interface ValidateCardInParkingProps extends React.HTMLAttributes<HTMLDivElement> {
    inParkingCard: InParkingCard;
    refetch: () => void;
    validatorSettings: ValidatorSettings;
}

type FormSchemaValues = z.infer<typeof formSchema>;
const formSchema = z.object({
    validation_reason: z.string().nonempty().default(null),
});

const ValidateModal = ({ children, inParkingCard, refetch, validatorSettings }: ValidateCardInParkingProps) => {
    const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();
    const { guest_reasons: guestReasonIds, allowed_validation: allowedValidation } = validatorSettings;

    const { mutate: validateCardInParking, isPending } = useValidateCardInParking({
        onSuccess: (_) => {
            refetch();
            setIsModalOpen(false);
            toast({
                title: formatMessage(validationMessages.validationSuccess),
                description: formatMessage(validationMessages.validationSuccessDetails, {
                    vehicle: inParkingCard.plate_number,
                }),
            });
        },
        onError: (error) => {
            const errorData = error.response?.data;
            const errorCode = errorData?.code ?? formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorData?.message ?? formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const defaultValues = React.useMemo(
        () => ({ validation_reason: guestReasonIds.length === 1 ? guestReasonIds[0] : null }),
        [guestReasonIds],
    );

    const { data: guestReasonsData } = useGetParkingGuestReasons({
        parkingId: parkingId || '',
        config: { enabled: !!parkingId },
    });

    const currentUserReasons = React.useMemo(() => {
        if (!guestReasonsData) return [];
        if (guestReasonIds.length < 1) return guestReasonsData.results;
        return guestReasonsData.results.filter((reason) => guestReasonIds.includes(reason.id));
    }, [guestReasonsData, guestReasonIds]);

    const onSubmit = (values: FormSchemaValues) => {
        const submitData = {
            parkingId,
            cardId: inParkingCard.id,
            ...values,
        };

        validateCardInParking(submitData);
    };

    const form = useForm<FormSchemaValues>({
        resolver: allowedValidation === ALLOWED_VALIDATION.GUEST ? zodResolver(formSchema) : undefined,
        mode: 'onChange',
        defaultValues,
        values: defaultValues,
        resetOptions: {
            keepDirtyValues: false,
        },
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [form, defaultValues, isModalOpen]);

    return (
        <Dialog modal open={isModalOpen} onOpenChange={() => setIsModalOpen(!isModalOpen)}>
            {children}
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{`${formatMessage(validationMessages.validate)} ${inParkingCard.card_number}`}</DialogTitle>
                </DialogHeader>
                <DialogSeparator />
                {allowedValidation === ALLOWED_VALIDATION.GUEST && guestReasonIds.length !== 1 ? (
                    <Form form={form}>
                        <FormField
                            control={form.control}
                            name="validation_reason"
                            render={({ field }) => (
                                <FormItem variant="dialog">
                                    <FormLabel variant="dialog" required>
                                        {formatMessage(validationMessages.validationReason)}
                                    </FormLabel>
                                    <FormControlStack className="mr-3">
                                        <FormControl>
                                            <Select onValueChange={field.onChange} value={field.value ?? undefined}>
                                                <SelectTrigger className="w-full">
                                                    <SelectValue
                                                        placeholder={formatMessage(validationMessages.validationReason)}
                                                    />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {currentUserReasons.map((reason) => (
                                                        <SelectItem key={reason.id} value={reason.id}>
                                                            {reason.name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </FormControl>
                                        <FormMessage />
                                    </FormControlStack>
                                </FormItem>
                            )}
                        />
                    </Form>
                ) : (
                    <DialogBody>{formatMessage(validationMessages.validationConfirmation)}</DialogBody>
                )}
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            {formatMessage(generalMessages.cancel)}
                        </Button>
                    </DialogClose>
                    <Button type="submit" onClick={form.handleSubmit(onSubmit)} className="flex-1">
                        {isPending ? (
                            <FontAwesomeIcon icon={faSpinnerThird} className="mr-2 size-4 animate-spin" />
                        ) : null}
                        {formatMessage(validationMessages.validate)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export { ValidateModal };
